import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_TITLE,
  OG_IMAGE_URL,
  SITE_LOCALE,
  SITE_NAME,
  absoluteUrl,
  getSeoForPath,
} from '../constants/seo'

function upsertMeta(
  selector: string,
  create: () => HTMLMetaElement,
  content: string
): void {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!element) {
    element = create()
    document.head.appendChild(element)
  }
  element.content = content
}

function upsertLink(rel: string, href: string): void {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    document.head.appendChild(element)
  }
  element.href = href
}

const SiteSeo = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    const seo = getSeoForPath(pathname)
    const canonical = absoluteUrl(seo.path)
    const title = seo.title || DEFAULT_TITLE
    const description = seo.description || DEFAULT_DESCRIPTION
    const robots = seo.robots ?? 'index, follow'
    const ogType = seo.ogType ?? 'website'

    document.title = title

    upsertMeta('meta[name="description"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'description'
      return meta
    }, description)

    upsertMeta('meta[name="keywords"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'keywords'
      return meta
    }, DEFAULT_KEYWORDS)

    upsertMeta('meta[name="robots"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'robots'
      return meta
    }, robots)

    upsertMeta('meta[property="og:title"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:title')
      return meta
    }, title)

    upsertMeta('meta[property="og:description"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:description')
      return meta
    }, description)

    upsertMeta('meta[property="og:url"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:url')
      return meta
    }, canonical)

    upsertMeta('meta[property="og:type"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:type')
      return meta
    }, ogType)

    upsertMeta('meta[property="og:site_name"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:site_name')
      return meta
    }, SITE_NAME)

    upsertMeta('meta[property="og:locale"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:locale')
      return meta
    }, SITE_LOCALE)

    upsertMeta('meta[property="og:image"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:image')
      return meta
    }, OG_IMAGE_URL)

    upsertMeta('meta[property="og:image:alt"]', () => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'og:image:alt')
      return meta
    }, 'Zimark — AI homework workspace with Lucas')

    upsertMeta('meta[name="twitter:card"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'twitter:card'
      return meta
    }, 'summary_large_image')

    upsertMeta('meta[name="twitter:title"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'twitter:title'
      return meta
    }, title)

    upsertMeta('meta[name="twitter:description"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'twitter:description'
      return meta
    }, description)

    upsertMeta('meta[name="twitter:image"]', () => {
      const meta = document.createElement('meta')
      meta.name = 'twitter:image'
      return meta
    }, OG_IMAGE_URL)

    upsertLink('canonical', canonical)
  }, [pathname])

  return null
}

export default SiteSeo
